import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { useThemeContext } from '../../contexts/ThemeContext';
import type { TutorLessonOption } from '../../models/Tutor';
import type { HostBookingClientOption } from '../../models/HostOrganizationUser';
import BookingClientSelect from './BookingClientSelect';
import TutorLessonOptionPicker from '../tutor/TutorLessonOptionPicker';
import {
  DEFAULT_MANUAL_BOOKING_COUNTRY_CODE,
  DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH,
  isManualBookingNewClient,
  MANUAL_BOOKING_NEW_CLIENT,
} from '../../utils/manualHostBooking';
import BookingOccurrenceEditor from '../booking/BookingOccurrenceEditor';
import ManualBookingPriceSummary from '../booking/ManualBookingPriceSummary';
import { bookingSessionsOverlap } from '../../utils/bookingRepeatSlots';
import type { DiscountSettings } from '../../models/Organization';

export interface HostTutorWalkInDialogProps {
  open: boolean;
  onClose: () => void;
  lessonOptions: TutorLessonOption[];
  bookingClients: HostBookingClientOption[];
  bookingClientsLoading?: boolean;
  selectedClientId: string;
  setSelectedClientId: (id: string) => void;
  guestPhoneLocal: string;
  setGuestPhoneLocal: (value: string) => void;
  sendBookingLinkSms: boolean;
  setSendBookingLinkSms: (value: boolean) => void;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedTime: Date | null;
  setSelectedTime: (time: Date | null) => void;
  extraSessionStarts: Date[];
  setExtraSessionStarts: (starts: Date[]) => void;
  lessonOptionId: string;
  setLessonOptionId: (id: string) => void;
  isPast: boolean;
  hasConflict: boolean;
  submitting?: boolean;
  onSubmit: () => void;
  /** Lesson currency fallback when the option has no currency. */
  currencyCode?: string | null;
  /** Organization single-booking discount, applied to the price shown before submit. */
  discountSettings?: DiscountSettings | null;
}

const fieldSx = (mode: string) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
    },
  },
});

export default function HostTutorWalkInDialog({
  open,
  onClose,
  lessonOptions,
  bookingClients,
  bookingClientsLoading = false,
  selectedClientId,
  setSelectedClientId,
  guestPhoneLocal,
  setGuestPhoneLocal,
  sendBookingLinkSms,
  setSendBookingLinkSms,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  extraSessionStarts,
  setExtraSessionStarts,
  lessonOptionId,
  setLessonOptionId,
  isPast,
  hasConflict,
  submitting = false,
  onSubmit,
  currencyCode = null,
  discountSettings = null,
}: HostTutorWalkInDialogProps) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const sx = fieldSx(mode);

  const getCurrentLocale = () => {
    const lang = t('calendar.locale', 'en');
    if (lang === 'pl') return plPL;
    return enGB;
  };

  const clientId = selectedClientId || MANUAL_BOOKING_NEW_CLIENT;
  const selectedOption = lessonOptions.find((option) => option.id === lessonOptionId) ?? null;
  const sessionDurationMinutes = selectedOption?.durationMinutes ?? 60;
  const primaryStart =
    selectedDate && selectedTime
      ? new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          selectedTime.getHours(),
          selectedTime.getMinutes(),
          0,
          0,
        )
      : null;
  const sessionStarts = primaryStart ? [primaryStart, ...extraSessionStarts] : [];
  const sessionsOverlap = bookingSessionsOverlap(
    sessionStarts.map((start) => ({ start, durationMinutes: sessionDurationMinutes })),
  );
  const applySessionStarts = (next: Date[]) => {
    if (next.length === 0) return;
    const [first, ...rest] = next;
    setSelectedDate(new Date(first.getFullYear(), first.getMonth(), first.getDate()));
    setSelectedTime(new Date(first));
    setExtraSessionStarts(rest);
  };
  const canSubmit =
    Boolean(selectedDate && selectedTime && lessonOptionId && clientId) &&
    !isPast &&
    !hasConflict &&
    !sessionsOverlap &&
    !submitting &&
    lessonOptions.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          margin: { xs: 1, sm: 2 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: '600px' },
          boxShadow:
            mode === 'dark' ? '0px 8px 32px rgba(0, 0, 0, 0.6)' : '0px 4px 20px rgba(0, 0, 0, 0.12)',
          background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.5,
          pt: 2.5,
          px: 3,
          '& .MuiTypography-root': {
            fontWeight: 700,
            fontSize: '1.35rem',
            color: mode === 'dark' ? '#ECECFB' : '#212529',
            letterSpacing: '-0.02em',
          },
        }}
      >
        {t('spaceCalendar.createManualBooking', 'Create Booking')}
      </DialogTitle>
      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getCurrentLocale()}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%' }}>
            <BookingClientSelect
              label={t('spaceCalendar.bookingClientLabel', 'Client')}
              unnamedLabel={t('spaceCalendar.unnamedStudent', 'Student')}
              clients={bookingClients}
              value={clientId}
              onChange={(next) => {
                setSelectedClientId(next);
                if (!isManualBookingNewClient(next)) {
                  setGuestPhoneLocal('');
                }
              }}
              loading={bookingClientsLoading}
              disabled={submitting}
              sx={sx}
            />

            {!bookingClientsLoading && bookingClients.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t(
                  'spaceCalendar.noBookingClientsYet',
                  'No clients with booking history yet. You can still create a guest booking.',
                )}
              </Alert>
            ) : null}

            {isManualBookingNewClient(clientId) ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <TextField
                  label={t('spaceCalendar.guestPhoneLabel', 'Guest phone (optional)')}
                  value={guestPhoneLocal}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (new RegExp(`^\\d{0,${DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH}}$`).test(value)) {
                      setGuestPhoneLocal(value);
                    }
                  }}
                  fullWidth
                  sx={sx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {DEFAULT_MANUAL_BOOKING_COUNTRY_CODE}
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  sx={{
                    m: 0,
                    mt: 1,
                    width: '100%',
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      lineHeight: 1.3,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={sendBookingLinkSms}
                      onChange={(e) => setSendBookingLinkSms(e.target.checked)}
                    />
                  }
                  label={t('spaceCalendar.sendBookingLinkSms', 'Send booking link by SMS')}
                />
              </Box>
            ) : null}

            {lessonOptions.length === 0 ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {t(
                  'tutorCalendar.walkInNoLessonOptions',
                  'This tutor has no lesson options yet. Edit their listing first.',
                )}
              </Alert>
            ) : (
              <TutorLessonOptionPicker
                label={t('tutorCalendar.walkInLessonOption', 'Lesson option')}
                options={lessonOptions}
                value={lessonOptionId}
                onChange={setLessonOptionId}
                disabled={submitting}
                sx={sx}
              />
            )}

            {sessionStarts.length > 1 ? (
              <BookingOccurrenceEditor
                starts={sessionStarts}
                durationMinutes={sessionDurationMinutes}
                disabled={submitting}
                onChange={applySessionStarts}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  <DatePicker
                    label={t('spaceCalendar.date')}
                    value={selectedDate}
                    onChange={setSelectedDate}
                    disablePast
                    disabled={submitting}
                    slotProps={{ textField: { fullWidth: true, sx } }}
                  />
                  <TimePicker
                    label={t('spaceCalendar.time')}
                    value={selectedTime}
                    onChange={setSelectedTime}
                    ampm={false}
                    disabled={submitting}
                    slotProps={{ textField: { fullWidth: true, sx } }}
                  />
                </Box>
                {primaryStart ? (
                  <BookingOccurrenceEditor
                    starts={sessionStarts}
                    durationMinutes={sessionDurationMinutes}
                    disabled={submitting}
                    onChange={applySessionStarts}
                  />
                ) : null}
              </>
            )}

            {isPast ? (
              <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
                {t('spaceCalendar.pastBookingWarning')}
              </Alert>
            ) : null}

            {hasConflict && !isPast ? (
              <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
                {t(
                  'tutorCalendar.slotConflictWarning',
                  'This slot conflicts with an existing lesson or block.',
                )}
              </Alert>
            ) : null}

            {selectedOption && selectedDate && selectedTime && clientId ? (
              <ManualBookingPriceSummary
                lineLabel={selectedOption.name}
                sessionAmount={selectedOption.price}
                durationMinutes={selectedOption.durationMinutes}
                occurrenceCount={1 + extraSessionStarts.length}
                currencyCode={selectedOption.lessonPriceCurrency?.trim() || currencyCode?.trim() || 'PLN'}
                discountSettings={discountSettings}
              />
            ) : null}

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t(
                'spaceCalendar.tutorEventHint',
                'Creates a confirmed tutor lesson. Pick a student from your list, or leave as walk-in. Choose the lesson option.',
              )}
            </Alert>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <DialogActions sx={{ p: 3, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={submitting}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : '#4E67EB',
            color: mode === 'dark' ? '#ECECFB' : '#4E67EB',
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            backgroundColor: '#4E67EB',
            color: '#ffffff',
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {t('spaceCalendar.createManualBooking', 'Create Booking')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
